import '@beyondtrust/btux-iconography-web';
import { ContextConsumer } from '@lit/context';
import { localized, msg } from '@lit/localize';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import type SlMenuItem from '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import styles from './site-selector.styles.scss';

import { createSelectedSiteActionEvent } from '../../context';
import { sitesDataContext } from '../../context/sites-data';
import { SiteAccessibility } from '../../types';

export const SITE_SELECTOR_TAG = 'platform-site-selector' as const;

/** Show the search field only when there are more than this many sites. */
export const SITE_SEARCH_THRESHOLD = 10;
/** Debounce (ms) applied to the search field before the list filters. */
export const SITE_SEARCH_DEBOUNCE_MS = 400;

/**
 * Site Selector used to switch between the user's available Sites.
 */
@customElement(SITE_SELECTOR_TAG)
@localized()
export class SpogSiteSelector extends LitElement {
  @property({ type: String })
  selected!: string;

  @state()
  private _query = '';

  static override styles = [styles];

  _sitesData = new ContextConsumer(this, { context: sitesDataContext, subscribe: true });

  private _debounce?: ReturnType<typeof setTimeout>;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this._debounce);
  }

  private get _sites(): SiteAccessibility[] {
    return this._sitesData.value ?? [];
  }

  private get _filtered(): SiteAccessibility[] {
    const query = this._query.trim().toLowerCase();
    return query ? this._sites.filter((site) => site.name.toLowerCase().includes(query)) : this._sites;
  }

  private _selectedName(): string {
    return this._sites.find((site) => site.site_id === this.selected)?.name ?? '';
  }

  private _onSearch(event: Event) {
    const { value } = event.target as HTMLInputElement;
    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => (this._query = value), SITE_SEARCH_DEBOUNCE_MS);
  }

  // Keep typed characters in the search field; let navigation/dismiss keys drive
  // the dropdown + menu. Without this, sl-menu's type-ahead would steal keystrokes.
  private _onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.renderRoot.querySelector<SlMenuItem>('sl-menu-item')?.focus();
      return;
    }
    // Enter is a no-op in the search field: block the default action (form
    // submission when embedded in a <form>) and stop it reaching the menu.
    // Users arrow into the list to select.
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // Escape/Tab close or move focus and ArrowUp jumps to the last item, so let
    // those bubble. Printable keys only stop propagation, so they type into the
    // input instead of triggering the menu's type-ahead (sl-dropdown forwards
    // via getMenu()).
    if (['ArrowUp', 'Escape', 'Tab'].includes(event.key)) {
      return;
    }
    event.stopPropagation();
  }

  private _onSelect(event: CustomEvent<{ item: SlMenuItem }>) {
    const item = event.detail.item;
    if (!item?.value) {
      return;
    }
    const isCurrent = item.value === this.selected;
    // sl-menu toggles checkbox items on selection; undo that so the check
    // mirrors `selected` alone (re-clicking the current site keeps it checked;
    // a new site only checks once the host confirms).
    item.checked = isCurrent;
    // Re-selecting the current site is a no-op — the dropdown still closes, but
    // we suppress the switch so the host doesn't re-run the full re-auth nav.
    if (isCurrent) {
      return;
    }
    this.dispatchEvent(createSelectedSiteActionEvent(item.value));
  }

  // sl-dropdown keeps its panel in the DOM; focus the search once it is shown.
  private _onAfterShow() {
    if (this._sites.length > SITE_SEARCH_THRESHOLD) {
      this.renderRoot.querySelector<HTMLInputElement>('input.search')?.focus();
    }
  }

  private _onAfterHide() {
    // Cancel any in-flight debounce and clear the field so reopening starts
    // fresh — the native input keeps its text otherwise (the panel stays in the DOM).
    clearTimeout(this._debounce);
    this._query = '';
    const search = this.renderRoot.querySelector<HTMLInputElement>('input.search');
    if (search) {
      search.value = '';
    }
  }

  private _flag(residency: string) {
    return choose(
      residency.toLowerCase(),
      [
        ['us', () => html`<btux-svg name="flag-us"></btux-svg>`],
        ['eu', () => html`<btux-svg name="flag-eu"></btux-svg>`],
        ['ca', () => html`<btux-svg name="flag-canada"></btux-svg>`],
        ['br', () => html`<btux-svg name="flag-brazil"></btux-svg>`],
        ['uk', () => html`<btux-svg name="flag-uk"></btux-svg>`],
        ['au', () => html`<btux-svg name="flag-australia"></btux-svg>`],
        ['in', () => html`<btux-svg name="flag-india"></btux-svg>`],
        ['ae', () => html`<btux-svg name="flag-uae"></btux-svg>`],
        ['sg', () => html`<btux-svg name="flag-singapore"></btux-svg>`],
        ['za', () => html`<btux-svg name="flag-south-africa"></btux-svg>`],
        ['gov', () => html`<btux-svg name="fedramp"></btux-svg>`],
        ['global', () => html`<btux-svg name="gears"></btux-svg>`],
      ],
      // Unknown residency falls back to the gears icon (same as GLOBAL).
      () => html`<btux-svg name="gears"></btux-svg>`,
    );
  }

  // Option subtitle: the raw region code + localized "data residency"
  // (e.g. "US data residency"). Empty for gov/global, which have no region.
  private _residencyLabel(residency: string): string {
    const code = residency.toLowerCase();
    if (code === 'gov' || code === 'global') {
      return '';
    }
    return `${residency} ${msg('data residency', {
      id: 'siteSelectorDataResidency',
      desc: 'Suffix after a raw region code in a site option, e.g. "US data residency"',
    })}`;
  }

  // Inline SVG chevron — avoids depending on a btux-iconography icon name;
  // inherits the theme via currentColor.
  private _chevron() {
    return html`<svg
      class="trigger-chevron"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>`;
  }

  override render() {
    const showSearch = this._sites.length > SITE_SEARCH_THRESHOLD;
    const sites = this._filtered;
    const selectedName = this._selectedName();
    const siteLabel = msg('Site', {
      id: 'siteSelectorLabel',
      desc: 'Label announced for the site selector button, followed by the current site name',
    });
    const triggerLabel = selectedName ? `${siteLabel}: ${selectedName}` : siteLabel;
    const searchLabel = msg('Search sites', {
      id: 'siteSelectorSearchPlaceholder',
      desc: 'Placeholder and accessible label for the site selector search field',
    });

    return html`
      <sl-dropdown
        class="site-selector"
        distance="4"
        hoist
        @sl-select=${this._onSelect}
        @sl-after-show=${this._onAfterShow}
        @sl-after-hide=${this._onAfterHide}
      >
        <button type="button" slot="trigger" class="trigger" aria-label=${triggerLabel}>
          <span class="trigger-name">${selectedName}</span>
          ${this._chevron()}
        </button>

        <div class="panel">
          ${showSearch
            ? html`<input
                type="search"
                class="search"
                placeholder=${searchLabel}
                aria-label=${searchLabel}
                @input=${this._onSearch}
                @keydown=${this._onSearchKeydown}
              />`
            : nothing}
          ${sites.length === 0
            ? html`<div class="empty" role="status">
                ${msg('No sites found', {
                  id: 'siteSelectorNoSitesFound',
                  desc: 'Shown when the site selector search matches no sites',
                })}
              </div>`
            : html`<sl-menu>
                ${repeat(
                  sites,
                  (site) => site.site_id,
                  (site) => {
                    const residencyLabel = this._residencyLabel(site.residency);
                    return html`<sl-menu-item
                      class=${classMap({ 'site-option': true, selected: site.site_id === this.selected })}
                      type="checkbox"
                      ?checked=${site.site_id === this.selected}
                      value=${site.site_id}
                      data-dd-action-name="site-selector-option-${site.name}"
                    >
                      <span slot="prefix" class="flag">${this._flag(site.residency)}</span>
                      <span class="option-name">${site.name}</span>
                      ${residencyLabel ? html`<span class="option-residency">${residencyLabel}</span>` : nothing}
                    </sl-menu-item>`;
                  },
                )}
              </sl-menu>`}
        </div>
      </sl-dropdown>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SITE_SELECTOR_TAG]: SpogSiteSelector;
  }
}
